import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import type { TFunction } from 'i18next';
import i18n from '../../../../misc/i18n';
import type { WalletPosition } from '../../../../queries/pxtraCard';

export { berlinIsoDate, berlinYearMonth } from './berlinCalendar';

/**
 * Same output as `Util.formatCurrency(cents, true)`. `misc/util` builds the API client
 * when imported, which keeps these helpers out of the Node unit tests.
 */
export const formatCents = (cents: number) =>
  (cents / 100).toLocaleString(i18n.language === 'en' ? 'en-US' : 'de-DE', {
    style: 'currency',
    currency: 'EUR'
  });

/** Transactions carry their sign: `+1.000,00 €` / `−1.200,00 €` (typographic minus); zero stays bare. */
export const formatSignedCents = (cents: number) => {
  if (cents === 0) return formatCents(0);
  return cents < 0 ? `−${formatCents(Math.abs(cents))}` : `+${formatCents(cents)}`;
};

/** Coverage never shows a plus; a negative value keeps the typographic minus. */
export const formatCoverageCents = (cents: number) =>
  cents < 0 ? `−${formatCents(Math.abs(cents))}` : formatCents(cents);

const BERLIN = 'Europe/Berlin';

const isEnglish = () => i18n.language === 'en';

const intlLocale = () => (isEnglish() ? 'en-GB' : 'de-DE');

const berlinDate = (locale: string) =>
  new Intl.DateTimeFormat(locale, {
    timeZone: BERLIN,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

const berlinDayMonth = (locale: string) =>
  new Intl.DateTimeFormat(locale, { timeZone: BERLIN, day: '2-digit', month: '2-digit' });

/** `dd.MM.yyyy` on the Berlin calendar; a bare `YYYY-MM-DD` keeps its own date. */
export const formatDate = (iso: string) => berlinDate(intlLocale()).format(new Date(iso));

const berlinDateTime = (locale: string) =>
  new Intl.DateTimeFormat(locale, {
    timeZone: BERLIN,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  });

/** `dd.MM.yyyy, HH:mm` on the Berlin clock. */
export const formatDateTime = (instant: Date | number) =>
  berlinDateTime(intlLocale()).format(instant);

/** `30.09.`: the escalation copy names the deadline without its year. */
export const formatDayMonth = (iso: string) => berlinDayMonth(intlLocale()).format(new Date(iso));

/** `11/2026` for the month after the one `iso` falls in. */
export const formatMonthAfter = (iso: string) => {
  const [year, month] = iso.split('-').map(Number);
  const next = new Date(Date.UTC(year, month, 1));

  return `${String(next.getUTCMonth() + 1).padStart(2, '0')}/${next.getUTCFullYear()}`;
};

export const monthName = (yearMonth: string) =>
  format(parseISO(`${yearMonth}-01`), 'LLLL', { locale: isEnglish() ? enUS : de });

/** `Oktober 2026`, for copy that names a month and its year. */
export const monthYearName = (yearMonth: string) =>
  format(parseISO(`${yearMonth}-01`), 'LLLL yyyy', { locale: isEnglish() ? enUS : de });

/** `3 Positionen • 34 Personen`, each part in its own singular or plural. */
export const upcomingSummary = (t: TFunction, positions: number, persons: number) =>
  `${t('pxtraCard:upcomingPositions', { count: positions })} • ${t('pxtraCard:upcomingPersons', { count: persons })}`;

export const positionLabel = (t: TFunction, position: WalletPosition) =>
  position.kind === 'monthly_benefit'
    ? t('pxtraCard:positionMonthly', { month: monthName(position.due_month) })
    : position.label;
